import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  Lock, 
  Activity,
  Calendar,
  Download
} from 'lucide-react';
import axios from 'axios';

const Analytics = () => {
  const [analytics, setAnalytics] = useState({});
  const [lockers, setLockers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    try {
      const [analyticsRes, lockersRes] = await Promise.all([
        axios.get('/api/analytics/usage'),
        axios.get('/api/lockers')
      ]);
      setAnalytics(analyticsRes.data);
      setLockers(lockersRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for charts (in production, this would come from the backend)
  const statusData = [
    { name: 'Available', value: analytics.available || 0, color: '#30d158' },
    { name: 'Occupied', value: analytics.occupied || 0, color: '#ff9f0a' },
    { name: 'Maintenance', value: analytics.maintenance || 0, color: '#ff453a' },
  ];

  const locationData = [
    { location: 'Men\'s Locker Room', available: 2, occupied: 1, maintenance: 1 },
    { location: 'Women\'s Locker Room', available: 1, occupied: 1, maintenance: 0 },
  ];

  const usageData = [
    { day: 'Mon', usage: 65 },
    { day: 'Tue', usage: 72 },
    { day: 'Wed', usage: 68 },
    { day: 'Thu', usage: 75 },
    { day: 'Fri', usage: 80 },
    { day: 'Sat', usage: 85 },
    { day: 'Sun', usage: 70 },
  ];

  const chartAxis = { stroke: 'rgba(235,235,245,0.4)', fontSize: 12 };
  const chartTooltip = {
    contentStyle: {
      background: 'rgba(28, 28, 36, 0.92)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '12px',
      color: '#fff',
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="dashboard-header">Analytics</h1>
        <p className="text-secondary mt-2">Detailed insights into locker usage and performance</p>
      </div>

      {/* Time Range Selector (segmented control) */}
      <div className="flex justify-between items-center mb-6">
        <div className="inline-flex material-thin rounded-xl p-1 space-x-1">
          {[
            { id: '24h', label: '24 Hours' },
            { id: '7d', label: '7 Days' },
            { id: '30d', label: '30 Days' },
          ].map((range) => (
            <button
              key={range.id}
              onClick={() => setTimeRange(range.id)}
              className={`px-4 py-1.5 rounded-lg text-subheadline font-medium transition-all duration-200 ease-spring ${
                timeRange === range.id
                  ? 'bg-white/[0.14] text-white shadow-card'
                  : 'text-secondary hover:text-white'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        <button className="btn btn-secondary">
          <Download className="w-4 h-4 mr-2" />
          <span>Export Report</span>
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="metric-card">
          <div className="flex items-center">
            <div className="p-2.5 bg-primary-500/15 rounded-xl">
              <TrendingUp className="w-6 h-6 text-primary-400" />
            </div>
            <div className="ml-4">
              <p className="metric-label">Utilization Rate</p>
              <p className="metric-value">{analytics.utilization || 0}%</p>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="flex items-center">
            <div className="p-2.5 bg-success-500/15 rounded-xl">
              <Activity className="w-6 h-6 text-success-400" />
            </div>
            <div className="ml-4">
              <p className="metric-label">24h Usage</p>
              <p className="metric-value">{analytics.usage24h || 0}</p>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="flex items-center">
            <div className="p-2.5 bg-warning-500/15 rounded-xl">
              <Calendar className="w-6 h-6 text-warning-400" />
            </div>
            <div className="ml-4">
              <p className="metric-label">7d Usage</p>
              <p className="metric-value">{analytics.usage7d || 0}</p>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="flex items-center">
            <div className="p-2.5 bg-danger-500/15 rounded-xl">
              <Lock className="w-6 h-6 text-danger-400" />
            </div>
            <div className="ml-4">
              <p className="metric-label">Maintenance</p>
              <p className="metric-value">{analytics.maintenance || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Locker Status Distribution */}
        <div className="card">
          <h3 className="text-title3 text-white mb-4">Locker Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                stroke="rgba(255,255,255,0.1)"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip {...chartTooltip} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Usage Trend */}
        <div className="card">
          <h3 className="text-title3 text-white mb-4">Weekly Usage Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={usageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="day" {...chartAxis} />
              <YAxis {...chartAxis} />
              <Tooltip {...chartTooltip} />
              <Legend wrapperStyle={{ color: 'rgba(235,235,245,0.6)' }} />
              <Line 
                type="monotone" 
                dataKey="usage" 
                stroke="#0a84ff" 
                strokeWidth={2.5}
                dot={{ fill: '#0a84ff', strokeWidth: 0 }}
                name="Usage %"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Location-based Analytics */}
      <div className="card mb-8">
        <h3 className="text-title3 text-white mb-4">Locker Usage by Location</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={locationData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis dataKey="location" {...chartAxis} />
            <YAxis {...chartAxis} />
            <Tooltip {...chartTooltip} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Legend wrapperStyle={{ color: 'rgba(235,235,245,0.6)' }} />
            <Bar dataKey="available" fill="#30d158" name="Available" radius={[6, 6, 0, 0]} />
            <Bar dataKey="occupied" fill="#ff9f0a" name="Occupied" radius={[6, 6, 0, 0]} />
            <Bar dataKey="maintenance" fill="#ff453a" name="Maintenance" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-title3 text-white mb-4">Top Performing Lockers</h3>
          <div className="space-y-3">
            {lockers
              .filter(locker => locker.lastUsed)
              .sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed))
              .slice(0, 5)
              .map((locker, index) => (
                <div key={locker.id} className="flex items-center justify-between p-3 material-thin rounded-xl">
                  <div className="flex items-center space-x-3">
                    <span className="text-footnote font-medium text-muted">#{index + 1}</span>
                    <div>
                      <p className="text-subheadline font-medium text-white">{locker.name}</p>
                      <p className="text-caption text-muted">{locker.location}</p>
                    </div>
                  </div>
                  <span className="text-footnote text-secondary">
                    {new Date(locker.lastUsed).toLocaleDateString()}
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="card">
          <h3 className="text-title3 text-white mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {lockers
              .filter(locker => locker.lastUsed)
              .sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed))
              .slice(0, 5)
              .map((locker) => (
                <div key={locker.id} className="flex items-center justify-between p-3 material-thin rounded-xl">
                  <div>
                    <p className="text-subheadline font-medium text-white">{locker.name}</p>
                    <p className="text-caption text-muted mt-1">
                      <span className={`status-badge ${locker.status === 'occupied' ? 'status-occupied' : 'status-available'}`}>
                        {locker.status}
                      </span>
                    </p>
                  </div>
                  <span className="text-footnote text-secondary">
                    {new Date(locker.lastUsed).toLocaleTimeString()}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 